/**
 * useSceneUnaware - who STEALTH'S LADDER says has not noticed the party,
 * and what that does to the next attack.
 *
 * A condition (`Unw`) is one way to be unaware and is handled where the
 * target's conditions are read (`useShellAttackModifiers`). This is the
 * other: while the table is in a phase that wears the ladder, everyone in
 * the opposition the ladder calls unaware. It is DERIVED, never written
 * onto anybody - the ladder moves, and so does the answer.
 *
 * It runs after the phase is known, which is later than the attacker's own
 * modifiers are summed, so it hands back that sum and its list with the
 * Easy (+30) line added when the current target is one of them (and is not
 * already unaware by condition, which would count it twice).
 *
 * @param {object} args - roster, rosterTabs, activeTab, actor,
 *   effectiveCustomMods, attackMod, direction.
 * @returns {{ unawareIds: ReadonlySet<string>, effectiveCustomMods: object[], attackMod: number }}
 */
import { useMemo } from 'react';
import { layoutOf } from '../engine/phase/layout';
import { stealthOf } from '../engine/phase/frame';
import { isUnawareIn, rungKnows } from '../engine/stealth/ladder';
import { oppositionFor } from '../engine/stealth/opposition';
import { UNAWARE_MOD, isUnaware } from '../engine/unaware';

const NOBODY = new Set();

/** The opposition the ladder calls unaware, by id. The TABLE'S phase decides, not a GM's peek. */
function unawareIn(roster, frame, actorId) {
  if (!frame || !layoutOf(frame.phase).wearsRung) return NOBODY;
  const stealth = stealthOf(frame);
  const ids = oppositionFor(roster, frame.partyTeam, actorId)
    .filter((c) => isUnawareIn(stealth, c.id))
    .map((c) => c.id);
  return ids.length ? new Set(ids) : NOBODY;
}

/** Does the ladder know the party is here - the table in a phase that wears it, at Searching or above? */
export function ladderKnows(frame) {
  return !!frame && layoutOf(frame.phase).wearsRung && rungKnows(stealthOf(frame).rung);
}

export default function useSceneUnaware({
  roster,
  rosterTabs,
  activeTab,
  actor,
  effectiveCustomMods,
  attackMod,
  direction,
}) {
  const actorId = actor?.id ?? null;
  const { frame } = direction;
  const unawareIds = useMemo(() => unawareIn(roster, frame, actorId), [roster, frame, actorId]);
  const adds = useMemo(
    () => unawareIds.has(activeTab) && !isUnaware(rosterTabs.find((t) => t.id === activeTab)?.target),
    [unawareIds, activeTab, rosterTabs],
  );
  const mods = useMemo(
    () => (adds ? [...effectiveCustomMods, UNAWARE_MOD] : effectiveCustomMods),
    [adds, effectiveCustomMods],
  );
  return { unawareIds, effectiveCustomMods: mods, attackMod: attackMod + (adds ? UNAWARE_MOD.value : 0) };
}
